import React from 'react';
import {
  Wrapper,
  Overlay,
  Container,
  Header,
  BackButton,
  LinkCounter,
  LinkCounterLabel,
  Name,
  Links,
  ScrollableContainer,
} from 'components/canvas/components/side-drawer/style';
import DrawerLink from 'components/canvas/components/side-drawer/components/drawer-link';
import { useSideDrawerState } from 'state/side-drawer';
import { areLinksEqual } from 'state/canvas/node-linking';
import { useTranslation } from 'i18n';
import { notEmpty } from 'utils/js';
import { sortBy, isNull } from 'lodash';
import { SurfaceNodePointsTo, SurfaceNodeUnion } from 'graphql/types';

function SideDrawer() {
  const { t } = useTranslation();
  const [isVisible, setVisibility] = React.useState<boolean>(false);
  const [node, { removeNodeFromDrawer }] = useSideDrawerState();
  const [globalLinks, setGlobalLinks] = React.useState<SurfaceNodePointsTo[]>(
    filterGlobalLinks(node),
  );

  const hasGlobalLinks = notEmpty(globalLinks);

  React.useEffect(() => {
    setGlobalLinks(filterGlobalLinks(node));
  }, [node]);

  // Animations
  React.useEffect(() => {
    setTimeout(() => setVisibility(hasGlobalLinks), 200);
  }, [hasGlobalLinks]);

  function filterGlobalLinks(node?: SurfaceNodeUnion): SurfaceNodePointsTo[] {
    if (node) {
      // @ts-ignore
      return node?.pointsTo?.filter<SurfaceNodePointsTo[]>(
        (link) =>
          link.to?.surface?.id !== node?.surface?.id ||
          (isNull(link.to?.surface) && isNull(node?.surface)),
      ) as SurfaceNodePointsTo[];
    }

    return [];
  }

  function removeGlobalLink(linkToRemove: SurfaceNodePointsTo): void {
    setGlobalLinks(
      globalLinks.filter(
        (globalLink) => areLinksEqual(globalLink, linkToRemove) === false,
      ),
    );
  }

  function closeDrawer() {
    setVisibility(false);
    setTimeout(removeNodeFromDrawer, 200);
  }

  if (hasGlobalLinks && node) {
    const { name, surface } = node;

    const sortedLinks = sortBy(globalLinks, ({ to }) => to?.surface?.name);

    return (
      <Wrapper isVisible={isVisible}>
        <Overlay onClick={closeDrawer} />
        <Container>
          <Header>
            <BackButton onClick={closeDrawer}>
              {surface?.name
                ? t('Back to {{surface}}', {
                    replace: { surface: surface?.name },
                  })
                : t('Back')}
            </BackButton>
            <LinkCounter>
              <LinkCounterLabel>
                {t('{{count}} Link(s) with', {
                  replace: { count: globalLinks.length },
                })}
              </LinkCounterLabel>
              <Name>{name}</Name>
            </LinkCounter>
          </Header>
          <Links>
            <ScrollableContainer>
              {sortedLinks.map((link, index) => (
                <DrawerLink
                  key={link.to.id}
                  node={node}
                  link={link}
                  linkNumber={index + 1}
                  removeGlobalLink={removeGlobalLink}
                  closeDrawer={closeDrawer}
                />
              ))}
            </ScrollableContainer>
          </Links>
        </Container>
      </Wrapper>
    );
  } else {
    return null;
  }
}

export default SideDrawer;
